import React, {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Button, DropdownProps, Icon } from "semantic-ui-react";

import moment from "moment";
import { useIntl } from "react-intl";
import { ComponentPropsGetterR } from "react-table-6";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import ModConfirmCNMI from "react-lib/apps/common/cnmi/ModConfirm";
import ModalItemFilter from "react-lib/apps/common/ModalItemFilter";

import CardEmployeeDocumentUX from "./CardEmployeeDocumentUX";

import {
  ACTIONS,
  BTN_ACTS,
  PickedState,
  RunSequence,
  SetProp,
  State,
} from "./sequence/EmployeeDocument";

// Types
type CardEmployeeDocumentProps = Pick<PickedState, "buttonLoadCheck"> & {
  setProp: SetProp;
  runSequence: RunSequence;
  EmployeeDocumentSequence: State["EmployeeDocumentSequence"];
  readOnly?: boolean;
};

const CardEmployeeDocument = (props: CardEmployeeDocumentProps) => {
  const intl = useIntl();

  const [openModFilterDocName, setOpenModFilterDocName] = useState(false);
  const [modConfirmDelete, setModConfirmDelete] = useState<number | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    props.runSequence({ sequence: "EmployeeDocument", restart: true });
  }, []);

  const handleChangeFilter = useCallback(async (value: any) => {
    props.runSequence({
      sequence: "EmployeeDocument",
      action: ACTIONS.CHANGE_FILTER,
      key: "documentFilterList",
      value,
    });

    setOpenModFilterDocName(false);
  }, []);

  const headers = useMemo(() => {
    const cloneItems = props.EmployeeDocumentSequence?.cloneItems ?? [];
    const documentFilterList = props.EmployeeDocumentSequence?.documentFilterList ?? [];

    return [
      <div
        key="employee-document-filter"
        style={{
          alignItems: "baseline",
          display: "flex",
          justifyContent: "center",
          position: "relative",
        }}
      >
        Document
        <Icon
          style={{
            color: cloneItems.length === documentFilterList.length ? "" : "yellow",
            cursor: "pointer",
            marginLeft: "5px",
            zIndex: 2,
          }}
          name="filter"
          onClick={() => {
            setOpenModFilterDocName(true);
          }}
        />
        {openModFilterDocName && (
          <ModalItemFilter
            style={{ margin: "1.75rem 0 0 13rem" }}
            data={cloneItems}
            filters={documentFilterList}
            name="Document Name"
            placeholder="Search Document Name"
            textField="document_name"
            onClose={() => {
              setOpenModFilterDocName(false);
            }}
            onSelect={handleChangeFilter}
          />
        )}
      </div>,
      "File name",
      "Upload date",
      "Delete",
    ];
  }, [openModFilterDocName, props.EmployeeDocumentSequence?.items]);

  const formattedItems = useMemo(
    () =>
      (props.EmployeeDocumentSequence?.items ?? []).map((item) => {
        const fileName = /\/([^/]+(?:\.pdf|.jpg|.jpeg|.png))/.exec(item.file)?.[1] || "";

        return {
          ...item,
          action: (
            <div style={{ display: "grid", placeContent: "center" }}>
              <Button
                style={{ fontSize: "0.65rem" }}
                color="red"
                icon="minus"
                size="mini"
                onClick={(e) => {
                  e.stopPropagation();

                  if (item.id) {
                    setModConfirmDelete(item.id);
                  }
                }}
              />
            </div>
          ),
          file_name: decodeURIComponent(fileName),
          upload_date: moment(item.created).format("DD/MM/YYYY"),
        };
      }),
    [props.EmployeeDocumentSequence?.items]
  );

  const handleChangeDocumentType = (e: SyntheticEvent, data: DropdownProps) => {
    props.setProp("EmployeeDocumentSequence.documentType", Number(data.value));
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      props.runSequence({
        sequence: "EmployeeDocument",
        action: ACTIONS.UPLOAD,
        files: e.target.files,
      });

      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const handleClickUploadFile = () => {
    inputRef.current?.click();
  };

  const handleConfirmDelete = () => {
    if (modConfirmDelete) {
      props.runSequence({
        sequence: "EmployeeDocument",
        action: ACTIONS.DELETE,
        id: modConfirmDelete,
        onSuccess: () => {
          handleCloseModConfirmDelete();
        },
      });
    }
  };

  const handleCloseModConfirmDelete = () => {
    setModConfirmDelete(null);
  };

  const handleGetTrProps: ComponentPropsGetterR = (state, rowInfo) => {
    const docId = props.EmployeeDocumentSequence?.selectedDocument?.id;
    const originalId = rowInfo?.original?.id;

    return {
      className: docId && docId === originalId ? "blueSelectedRow" : "",
      onClick: () => {
        const items = props.EmployeeDocumentSequence?.items ?? [];
        const foundItem = items.find((item) => item.id === originalId);

        if (foundItem) {
          props.runSequence({
            sequence: "EmployeeDocument",
            action: ACTIONS.SELECT,
            data: foundItem,
          });
        }
      },
    };
  };

  return (
    <div style={{ marginTop: "" }}>
      <CardEmployeeDocumentUX
        documentType={props.EmployeeDocumentSequence?.documentType}
        headers={headers}
        isDocLoading={!!props.buttonLoadCheck?.[BTN_ACTS.INIT]}
        readOnly={props.readOnly}
        tableData={formattedItems}
        documentTypeOptions={props.EmployeeDocumentSequence?.documentTypeOptions}
        getTrProps={handleGetTrProps}
        onChangeDocumentType={handleChangeDocumentType}
        onClickUploadFile={handleClickUploadFile}
        buttonUpload={
          <ButtonLoadCheck
            style={{ minWidth: "max-content" }}
            color="green"
            disabled={!props.EmployeeDocumentSequence?.documentType || props.readOnly}
            paramKey={BTN_ACTS.UPLOAD}
            size="small"
            title="Upload file"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.UPLOAD]}
            onClick={handleClickUploadFile}
          />
        }
        documentViewer={
          <FrameChrome file={props.EmployeeDocumentSequence?.selectedDocument?.file || ""} />
        }
      />

      <input
        ref={inputRef}
        accept=".png, .jpg, .jpeg, .pdf, application/pdf"
        type="file"
        hidden
        multiple
        onChange={handleFileInputChange}
      />

      <ModConfirmCNMI
        approveButtonLoading={!!props.buttonLoadCheck?.[BTN_ACTS.DELETE]}
        openModal={!!modConfirmDelete}
        titleColor="red"
        onApprove={handleConfirmDelete}
        onDeny={handleCloseModConfirmDelete}
        content={
          <div
            style={{
              fontWeight: "bold",
              margin: "0rem 0 -1rem",
              textAlign: "center",
            }}
          >
            {intl.formatMessage({ id: "ต้องการลบรายการนี้หรือไม่" })}
          </div>
        }
      />
    </div>
  );
};

/* ====================================================== */
/*                       FrameChrome                      */
/* ====================================================== */

const FrameChrome = ({ file }: { file: string }) => {
  if (!file) {
    return null;
  }

  const frame = `<iframe src="${file}" width="100%" height="100%"></iframe>`;

  return (
    <div style={{ height: "100%", width: "100%" }} dangerouslySetInnerHTML={{ __html: frame }} />
  );
};

CardEmployeeDocument.displayName = "CardEmployeeDocument";

export default React.memo(CardEmployeeDocument);
